import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {AuthUserType} from '@crema/types/models/AuthUser';
import jwtAxios, {setAuthToken} from './index';
import {useInfoViewActionsContext} from '@crema/context/AppContextProvider/InfoViewContextProvider';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch} from 'toolkit/hooks';
import {
  SHOW_ERROR_MESSAGE,
  SHOW_MESSAGE,
} from '@crema/types/actions/Common.action';
import {
  GET_ACADEMY_DATA,
  GET_ANALYTICS_DATA,
  GET_ECOMMERCE_DATA,
  SHOW_MODAL,
} from '@crema/constants/ActionTypes';
import {
  GET_ADMIN_ANALYTICS,
  GET_ALL_CATEGORIES,
  GET_ALL_COURSES,
  GET_ALL_SUPPORT_CHAT_USERS,
  GET_ALL_USERS,
  GET_CATEGORY_DATA,
  GET_COURSES_LISTING,
  GET_CRM_DATA,
  GET_CRYPTO_DATA,
  GET_CURRENT_COURSE,
  GET_HEALTH_CARE_DATA,
  GET_METRICS_DATA,
  GET_NEW_COURSES,
  GET_PENDING_COURSES,
  GET_TEACHERS_LISTING,
  GET_WIDGETS_DATA,
  GET_WITHDRAWAL_NOTIFICATIONS,
  SET_ALL_CHAT_USERS,
  SET_CAT_DATA,
  SET_CHAT_DATA,
  SET_CHAT_USER_IDS,
  SET_COURSE_CONTENT,
  SET_SELECTED_CHAT_USER,
  SET_SPECIFIC_REVIEW,
  SET_SUPPORT_AGENTS,
  SHOW_PROMO_MODAL,
} from '@crema/types/actions/Dashboard.action';

interface JWTAuthContextProps {
  user: AuthUserType | null | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface SignUpProps {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface SignInProps {
  email: string;
  password: string;
}

interface JWTAuthActionsProps {
  signUpUser: (data: SignUpProps) => void;
  signInUser: (data: SignInProps) => void;
  logout: () => void;
}

const JWTAuthContext = createContext<JWTAuthContextProps>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
});
const JWTAuthActionsContext = createContext<JWTAuthActionsProps>({
  signUpUser: () => {},
  signInUser: () => {},
  logout: () => {},
});

export const useJWTAuth = () => useContext(JWTAuthContext);

export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

interface JWTAuthAuthProviderProps {
  children: ReactNode;
}

const JWTAuthAuthProvider: React.FC<JWTAuthAuthProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [firebaseData, setJWTAuthData] = useState<JWTAuthContextProps>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const infoViewActionsContext = useInfoViewActionsContext();

  useEffect(() => {
    const getAuthUser = async () => {
      const token = localStorage.getItem('JWTnmoAdmin');

      if (!token) {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
        return;
      }
      setAuthToken(token);
      try {
        const {data} = await jwtAxios.get('/user/getMe');
        setJWTAuthData({
          user: data.responseData,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        setJWTAuthData({
          user: undefined,
          isLoading: false,
          isAuthenticated: false,
        });
      }
    };

    getAuthUser();
  }, []);

  const signInUser = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    infoViewActionsContext.fetchStart();
    try {
      const {data} = await jwtAxios.post('/auth/admin/login', {
        email,
        password,
      });

      localStorage.setItem('JWTnmoAdmin', data.responseData.token);
      setAuthToken(data.responseData.token);
      const res = await jwtAxios.get('/user/getMe');

      setJWTAuthData({
        user: res.data.responseData,
        isAuthenticated: true,
        isLoading: false,
      });
      infoViewActionsContext.fetchSuccess();
      dispatch({type: SHOW_MESSAGE, payload: 'Successfully Login'});
    } catch (error) {
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      infoViewActionsContext.fetchError(
        error?.response?.data?.msg || 'Something went wrong',
      );
      dispatch({
        type: SHOW_ERROR_MESSAGE,
        payload: error.response.data.message,
      });
    }
  };

  const signUpUser = async ({
    name,
    email,
    password,
    confirmPassword,
  }: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    infoViewActionsContext.fetchStart();
    try {
      const {data} = await jwtAxios.post('auth/admin/signup', {
        name,
        email,
        password,
        confirmPassword,
      });
      // localStorage.setItem('JWTnmo', data.token);
      // setAuthToken(data.token);
      // const res = await jwtAxios.get('/auth');
      // setJWTAuthData({
      //   user: res.data,
      //   isAuthenticated: true,
      //   isLoading: false,
      // });
      infoViewActionsContext.fetchSuccess();
      navigate('/signin');
    } catch (error) {
      setJWTAuthData({
        ...firebaseData,
        isAuthenticated: false,
        isLoading: false,
      });
      infoViewActionsContext.fetchError('Something went wrong');
    }
  };

  const logout = async () => {
    localStorage.removeItem('JWTnmoAdmin');
    setAuthToken();
    setJWTAuthData({
      user: null,
      isLoading: false,
      isAuthenticated: false,
    });
    dispatch({type: SHOW_MESSAGE, payload: 'Logged Out'});
    localStorage.clear();
    sessionStorage.clear();
    dispatch({type: GET_ACADEMY_DATA, payload: null});
    dispatch({type: GET_ANALYTICS_DATA, payload: null});
    dispatch({type: GET_ECOMMERCE_DATA, payload: null});
    dispatch({type: GET_CRM_DATA, payload: null});
    dispatch({type: GET_CRYPTO_DATA, payload: null});
    dispatch({type: GET_HEALTH_CARE_DATA, payload: null});
    dispatch({type: GET_METRICS_DATA, payload: null});
    dispatch({type: GET_WIDGETS_DATA, payload: null});
    dispatch({type: GET_ALL_CATEGORIES, payload: []});
    dispatch({type: SET_SPECIFIC_REVIEW, payload: null});
    dispatch({type: SET_SELECTED_CHAT_USER, payload: null});
    dispatch({type: GET_CATEGORY_DATA, payload: []});
    dispatch({type: SET_CAT_DATA, payload: null});
    dispatch({type: GET_TEACHERS_LISTING, payload: []});
    dispatch({type: GET_COURSES_LISTING, payload: null});
    dispatch({type: GET_ADMIN_ANALYTICS, payload: null});
    dispatch({type: GET_ALL_COURSES, payload: []});
    dispatch({type: GET_ALL_USERS, payload: []});
    dispatch({type: GET_CURRENT_COURSE, payload: null});
    dispatch({type: SET_ALL_CHAT_USERS, payload: []});
    dispatch({type: GET_NEW_COURSES, payload: []});
    dispatch({type: SET_SUPPORT_AGENTS, payload: []});
    dispatch({type: SET_CHAT_DATA, payload: []});
    dispatch({type: GET_ALL_SUPPORT_CHAT_USERS, payload: []});
    dispatch({type: SET_CHAT_USER_IDS, payload: []});
    dispatch({type: GET_PENDING_COURSES, payload: []});
    dispatch({type: SET_COURSE_CONTENT, payload: null});
    dispatch({type: GET_WITHDRAWAL_NOTIFICATIONS, payload: []});
    dispatch({type: SHOW_PROMO_MODAL, payload: false});
    dispatch({type: SHOW_MODAL, payload: false});
  };

  return (
    <JWTAuthContext.Provider
      value={{
        ...firebaseData,
      }}
    >
      <JWTAuthActionsContext.Provider
        value={{
          signUpUser,
          signInUser,
          logout,
        }}
      >
        {children}
      </JWTAuthActionsContext.Provider>
    </JWTAuthContext.Provider>
  );
};
export default JWTAuthAuthProvider;
